import { StoreService } from 'projects/dop-core/src/public-api';
import { StoreKey } from 'src/app/core/store/ngrx-store.keys';
import { OrderSetupService } from 'src/app/features/order-setup/order-setup.service';
import { CountryService } from 'src/app/shared/data-service/country.service';
import { LabConfigurationService } from 'src/app/shared/data-service/lab-configuration.service';
import { OrderCommandService } from 'src/app/shared/data-service/order-command.service';
import { UserManagementDataService } from 'src/app/shared/data-service/user-management-data.service';
import { UserType } from 'src/app/shared/enums/user-type.enum';
import { Address } from 'src/app/shared/models/address';
import { CurrentOrder } from 'src/app/shared/models/current-order.model';
import { LabConfiguration } from 'src/app/shared/models/lab-configuration.model';
import { LabModel } from 'src/app/shared/models/lab.model';
import { LogisticAddress } from 'src/app/shared/models/logistic/address.model';
import { PickUpInformation } from 'src/app/shared/models/logistic/pickup-information.model';
import { ShipFrom } from 'src/app/shared/models/logistic/ship-from.model';
import { ShipTo } from 'src/app/shared/models/logistic/ship-to.model';
import { ShippingEstimatesRequest } from 'src/app/shared/models/logistic/shipping-estimates-request.model';
import { ShippingEstimate } from 'src/app/shared/models/logistic/shipping-estimates.model';
import { ShippingPackages } from 'src/app/shared/models/logistic/shipping-packages.model';
import { OrderShipping } from 'src/app/shared/models/logistic/shipping.model';
import { SizeUnit } from 'src/app/shared/models/logistic/size-unit.model';
import { WeightUnits } from 'src/app/shared/models/logistic/weight-units.model';
import { PatchUpdateRequest } from 'src/app/shared/models/patch-update-request.model';
import { OrderShippingDetails } from 'src/app/shared/models/shipping/shipping-details-request.model';
import { OrderShippingPackage } from 'src/app/shared/models/shipping/shipping-package-request.model';
import { OrderShippingPickup } from 'src/app/shared/models/shipping/shipping-pickup-details-request.model';
import { UserDetails } from 'src/app/shared/models/user-details.model';
import { CurrentOrderService } from 'src/app/shared/services/current-order.service';
import { HelperService } from 'src/app/shared/services/helper.service';
import { Injectable } from '@angular/core';
import {
    ADDRESS1, ADDRESS2, BILLINGCURRENCYCODE, CARRIERSERVICECODE, CITY, COMPANY, COUNTRY, DIMENSIONUNIT, DRYICEWEIGHT, DRYICEWEIGHTUNIT,
    EARLIESTPICKUPTIME, ESTIMATEDPRICE, HEIGHT, ISDRYICESHIPMENT, ISPICKUPREQUIRED, ISRETURNSHIPMENTOPTED, LATESTPICKUPTIME,
    LENGTH, NAME, PHONE, PICKUPCOMMENT, PICKUPDATE, QUANTITY, SERVICEOPTION, STATE, WEIGHT, WEIGHTUNIT, WEIGHTUNITKG, WEIGHTUNITLB, WIDTH,
    ZIPCODE
} from './constants/shipping-constants';
import { OrderShippingDetailsResponse } from 'src/app/shared/models/shipping/shipping-details-response.model';
import { OrderDataService } from 'src/app/shared/data-service/order-data.service';
import { ShippingDetailsCleanUpService } from 'src/app/shared/shipping-cleanup/shipping-details-cleanup.service';
import { CountryAddressFormatRequest } from 'src/app/shared/models/country-address-format-request.model';
import { ShippingEstimatesResponse } from 'src/app/shared/models/logistic/shipping-estimates-response.model';
import { ShippingResponse } from 'src/app/shared/models/logistic/shipping-response.model';
import { PackageWeightUnitValue } from 'src/app/shared/enums/package-weight-unit-value.enum';
import { BehaviorSubject } from 'rxjs';
import { UserAndAccountSpecificDetails } from 'src/app/shared/models/user-and-account-specific-details';

@Injectable({
    providedIn: 'root'
})
export class AddShippingService {
    lab: LabModel;
    labConfiguration: LabConfiguration;
    
    pickUpTime = new PickUpTime();

    calendarDate = new BehaviorSubject<Date>(new Date());
    calendarDate$ = this.calendarDate.asObservable();
    
    get currentOrder(): CurrentOrder {
        return this.currentOrderService.currentOrder;
    }

    constructor(
        private orderCommandService: OrderCommandService,
        private orderDataService: OrderDataService,
        private currentOrderService: CurrentOrderService,
        private userManagementService: UserManagementDataService,
        private orderSetupService: OrderSetupService,
        private storeService: StoreService,
        private countryService: CountryService,
        private labConfigurationService: LabConfigurationService,
        private helperService: HelperService,
        private shippingDetailsCleanupService: ShippingDetailsCleanUpService) {
    }

    setCalendar(calendarDate: Date) {
        this.calendarDate.next(calendarDate);
      }
    
      getCalendar() {
        return this.calendarDate;
      }
      
    addOrUpdateShippingDetails(orderShippingDetails: OrderShippingDetails): Promise<any> {
        return new Promise((resolve) => {
            if (orderShippingDetails.dryIceWeight === null) {
                orderShippingDetails.dryIceWeight = 0;
            }
            this.orderCommandService.addOrUpdateShippingDetails(this.currentOrder.OrderCode, orderShippingDetails).subscribe(data => {
                if (!this.currentOrder.shipping) {
                    this.currentOrder.shipping = new OrderShipping();
                    this.currentOrder.shipping.orderPackageInformation = new Map<number, OrderShippingPackage>();
                }
                this.currentOrder.shipping.orderShippingDetails = orderShippingDetails;
                resolve(data);
            });
        });
    }

    deleteShippingDetails(): Promise<any> {
        return new Promise((resolve) => {
            this.orderCommandService.deleteShippingDetails(this.currentOrder.OrderCode).subscribe(data => {
                if (this.currentOrder.shipping) {
                    this.currentOrder.shipping = null;
                }
                resolve(data);
            });
        });
    }

    addPackage(packageId: number, quantity: number): Promise<number> {
        if (packageId > 0) {
            const defaultPackage: PatchUpdateRequest[] =
                [
                    { propertyName: 'quantity', updatedValue: quantity.toString() },
                    { propertyName: 'weightUnit', updatedValue: WeightUnits.KG },
                    { propertyName: 'dimensionUnit', updatedValue: SizeUnit.CM }
                ];

            return new Promise((resolve) => {
                this.orderCommandService.patchPackageDetails(packageId.toString(), defaultPackage)
                    .subscribe(data => {
                        resolve(packageId);
                    });
            });
        } else {
            const packages = Array.from(this.currentOrder.shipping.orderPackageInformation.values()).map(p => p);

            const orderShippingPackage = new OrderShippingPackage(null, null, null, null, quantity,
                packages.length > 0 ? packages[0].dimensionUnit : SizeUnit.CM,
                packages.length > 0 ? packages[0].weightUnit : WeightUnits.KG);

            return new Promise((resolve) => {
                this.orderCommandService.addNewPackage(this.currentOrder.OrderCode, orderShippingPackage)
                    .subscribe(data => {
                        this.currentOrder.shipping.orderPackageInformation.set(data.packageId, orderShippingPackage);
                        resolve(data.packageId);
                    });
            });
        }
    }

    getShippingPackages(): ShippingPackages[] {
        const shippingPackages: ShippingPackages[] = [];
        this.currentOrder.shipping.orderPackageInformation.forEach((op, keys) => {
            const shippingPackage = new ShippingPackages(op.id, op.quantity, 50, true,
                this.parseSizeUnit(op), this.parseWeightUnit(op));
            shippingPackage.dimensions.height = op.height;
            shippingPackage.dimensions.length = op.length;
            shippingPackage.dimensions.width = op.width;
            shippingPackage.weight = op.weight;
            shippingPackages.push(shippingPackage);
        });
        return shippingPackages;
    }

    private parseWeightUnit(op: OrderShippingPackage): WeightUnits {
        return op.weightUnit === '1' ? WeightUnits.LB : WeightUnits.KG;
    }

    private parseSizeUnit(op: OrderShippingPackage): SizeUnit {
        return op.dimensionUnit === '1' ? SizeUnit.IN : SizeUnit.CM;
    }

    checkAvailableOptions(accountCode: string): Promise<ShippingEstimatesResponse> {
        return new Promise((resolve, reject) => {
            const shipping = this.currentOrder.shipping;
            const request = new ShippingEstimatesRequest();

            request.carrierServiceCode = this.getRateCarrierCode();
            request.requireDryIceShipment = shipping.orderShippingDetails.isDryIceShipment;
            request.dryIceWeight = shipping.orderShippingDetails.dryIceWeight === null ?
                0 : shipping.orderShippingDetails.dryIceWeight;
            request.dryIceWeightUnit = shipping.orderShippingDetails.dryIceWeightUnit;
            request.shipFrom = this.getShipFrom(accountCode);
            this.setPickUpInformation(request);

            request.shipTo = this.getShipTo();
            if (request.shipTo === undefined) {
                const shippingEstimatesResponse = new ShippingEstimatesResponse();
                shippingEstimatesResponse.shippingResponse = new ShippingResponse();
                return resolve(shippingEstimatesResponse);
            }

            request.shippingPackages = this.getShippingPackages();

            this.orderDataService.getEstimates(request).subscribe(data => {
                resolve(data);
            },
            error => {
                reject(error);
            });
        });
    }

    public getShipFrom(accountCode: string): ShipFrom {

        const orderShippingPickup = this.currentOrder.shipping?.orderShippingPickup;

        if (orderShippingPickup.address) {
            const shipFrom = new ShipFrom();
            shipFrom.accountCode = accountCode;
            shipFrom.company = orderShippingPickup.address?.company;
            shipFrom.firstName = orderShippingPickup.address?.name?.split(' ')[0];
            shipFrom.lastName = orderShippingPickup.address?.name?.substr(orderShippingPickup.address?.name.indexOf(' ') + 1);
            shipFrom.phone = orderShippingPickup.address?.phonenumber;
            shipFrom.email = ''; // not present in UI
            shipFrom.address = new LogisticAddress(orderShippingPickup.address.line1, orderShippingPickup.address.line2,
                orderShippingPickup.address.city, orderShippingPickup.address.state,
                orderShippingPickup.address.country, orderShippingPickup.address.zipCode, '', '');
            shipFrom.shipmentDate = orderShippingPickup.shipmentDate;

            return shipFrom;
        }
    }

    private setPickUpInformation(request: ShippingEstimatesRequest) {
        if (this.currentOrder.shipping.orderShippingDetails.isPickupRequired) {
            request.shipFrom.pickUpInformation = new PickUpInformation();
            const orderShippingPickup = this.currentOrder.shipping?.orderShippingPickup;
            request.shipFrom.pickUpInformation.pickupInstruction = orderShippingPickup.pickupComment;

            if (orderShippingPickup.shipmentDate == null) {
                return;
            }
            
            request.shipFrom.shipmentDate = new Date(orderShippingPickup.shipmentDate)
            request.shipFrom.pickUpInformation.earliestPickupTime = orderShippingPickup.earliestPickupTime;
            request.shipFrom.pickUpInformation.latestPickupTime = orderShippingPickup.latestPickupTime;
        }
        else {
            request.shipFrom.pickUpInformation = null;
        }
    }

    public getShipTo(): ShipTo {
        const matchedCountry = this.countryService.getCountryLists()
            .find(e => e.name.toLowerCase() === this.lab?.country?.toLowerCase() ||
                e.code.toLowerCase() === this.lab?.country?.toLowerCase() ||
                'united states' === this.lab?.country?.toLowerCase() ||
                'united states of america' === this.lab?.country?.toLowerCase());

        if (this.lab) {
            const stateCode = this.helperService.getStateCode(this.lab.state);
            if (stateCode != null) {
                const address = new LogisticAddress(this.lab.streetNumberAndStreet, '', this.lab.city, stateCode, matchedCountry.code,
                    this.lab.zipCode, this.lab.country, '');
                const shipTo = new ShipTo();
                shipTo.labCode = this.lab.code;
                shipTo.company = this.lab.name;
                shipTo.contactName = ''; // Do not have this info
                shipTo.phone = ''; // Do not have this info
                shipTo.address = address;

                return shipTo;
            }
        }
    }

    getRateCarrierCode() {
        let carrierCodes = this.labConfiguration.carriers.map(t => t.code);
        if (this.currentOrder.shipping.orderShippingDetails.isDryIceShipment) {
            carrierCodes = this.getRateCarrierCodeDryIce(carrierCodes);
        }
        return carrierCodes;
    }

    private getRateCarrierCodeDryIce(carrierCodes) {
        const carriers = this.labConfiguration.carriers
            .filter(c => carrierCodes.includes(c.code) && c.carrierServices.some(s => s.dryIceShipmentSupported));
        return carriers.map(c => c.code);
    }

    patchPackage(propertyName: string, shippingPackage: ShippingPackages): Promise<number> {
        let patchReq: PatchUpdateRequest;
        switch (propertyName) {
            case WEIGHT:
                patchReq = new PatchUpdateRequest(propertyName, shippingPackage.weight);
                break;
            case WEIGHTUNIT:
                patchReq = new PatchUpdateRequest(propertyName, shippingPackage.weightUnit);
                break;
            case HEIGHT:
                patchReq = new PatchUpdateRequest(propertyName, shippingPackage.dimensions.height);
                break;
            case LENGTH:
                patchReq = new PatchUpdateRequest(propertyName, shippingPackage.dimensions.length);
                break;
            case WIDTH:
                patchReq = new PatchUpdateRequest(propertyName, shippingPackage.dimensions.width);
                break;
            case DIMENSIONUNIT:
                patchReq = new PatchUpdateRequest(propertyName, shippingPackage.dimensions.unit);
                break;
            case QUANTITY:
                patchReq = new PatchUpdateRequest(propertyName, shippingPackage.quantity);
                break;

            default:
                break;
        }

        return new Promise((resolve) => {
            this.orderCommandService.patchPackageDetails(shippingPackage.id.toString(), [patchReq])
                .subscribe(data => {
                    const orderShippingPackage = new OrderShippingPackage(shippingPackage.dimensions.width,
                        shippingPackage.dimensions.height, shippingPackage.dimensions.length,
                        shippingPackage.weight, shippingPackage.quantity, shippingPackage.dimensions.unit, shippingPackage.weightUnit);
                    this.currentOrder.shipping.orderPackageInformation.set(shippingPackage.id, orderShippingPackage);
                    resolve(shippingPackage.id);
                });
        });
    }

    deletePackage(packageId: number): Promise<number> {
        return new Promise((resolve) => {
            this.orderCommandService.deletePackage(packageId.toString()).subscribe(data => {
                this.currentOrder.shipping.orderPackageInformation.delete(packageId);
                resolve(packageId);
            });
        });
    }

    public async getLab(): Promise<LabModel> {
        return this.lab = await this.orderSetupService.getLab(this.currentOrder.quotation.receivingLabCode);
    }

    public async getFormattedAddress(addresses: CountryAddressFormatRequest): Promise<any> {
        return new Promise(async (resolve, reject) => {
            this.labConfigurationService.getFormattedAddress(addresses)
                .subscribe((data: string[]) => {
                    resolve(data);
                });
        });
    }

    public async getFormattedShippingAddress(address: Address): Promise<any> {
        const addresses: Address[] = [];
        const request = new CountryAddressFormatRequest();
        address.company = address.company ?? this.currentOrder.account.name;
        address.phonenumber = address.phonenumber ?? this.currentOrder.account.phoneNumber;
        addresses.push(address);
        request.Addresses = addresses;
        return this.getFormattedAddress(request);
    }

    public async getLabConfiguration(): Promise<LabConfiguration> {
        this.labConfigurationService.LabCode = this.lab.code;
        return this.labConfiguration = await this.labConfigurationService.getLabConfiguration().toPromise();
    }

    patchAddress(addressId: number, address: Address): Promise<number> {

        this.updateUserAndAccountSpecificDetails(address);
        const updateRequests: PatchUpdateRequest[] = [];
        if (address?.line1) {
            updateRequests.push(new PatchUpdateRequest(ADDRESS1, address.line1));
        }

        updateRequests.push(new PatchUpdateRequest(ADDRESS2, address.line2 ?? null));

        if (address?.city) {
            updateRequests.push(new PatchUpdateRequest(CITY, address.city));
        }
        if (address?.state) {
            updateRequests.push(new PatchUpdateRequest(STATE, address.state));
        }
        if (address?.country) {
            updateRequests.push(new PatchUpdateRequest(COUNTRY, address.country));
        }
        if (address?.zipCode) {
            updateRequests.push(new PatchUpdateRequest(ZIPCODE, address.zipCode));
        }

        return new Promise((resolve) => {
            this.orderCommandService.patchAddress(addressId, updateRequests).subscribe(data => {
                if (!this.currentOrder.shipping.orderShippingPickup) {
                    this.currentOrder.shipping.orderShippingPickup = new OrderShippingPickup();
                }
                this.currentOrder.shipping.orderShippingPickup.address = new Address();
                this.currentOrder.shipping.orderShippingPickup.address.company = address.company ?? this.currentOrder.account.name;
                this.currentOrder.shipping.orderShippingPickup.address.phonenumber = address.phonenumber ?? this.currentOrder.account.phoneNumber;
                this.currentOrder.shipping.orderShippingPickup.address.name = address.name;
                resolve(addressId);
            });
        });
    }

    updateUserAndAccountSpecificDetails(address: Address){
        var userAndAccountSpecificDetails = new UserAndAccountSpecificDetails(this.currentOrder.account.code, address);
        return new Promise((resolve) => {
            this.orderCommandService.updateUserAndAccountSpecificDetails(userAndAccountSpecificDetails).subscribe(data =>{
                resolve();
            });
        });
    }

    patchPickupMultiple(shippingPickup: OrderShippingPickup): Promise<any> {

        const updateRequests: PatchUpdateRequest[] = [];
        if (shippingPickup?.address?.company) {
            updateRequests.push(new PatchUpdateRequest(COMPANY, shippingPickup.address.company));
        }
        updateRequests.push(new PatchUpdateRequest(NAME, shippingPickup.address.name ?? null));

        if ((shippingPickup?.address?.phonenumber)) {
            updateRequests.push(new PatchUpdateRequest(PHONE, shippingPickup.address.phonenumber));
        }
        if (shippingPickup?.shipmentDate) {
            updateRequests.push(new PatchUpdateRequest(PICKUPDATE, this.helperService.getUTCISODate(shippingPickup.shipmentDate)));
        }
        
        updateRequests.push(new PatchUpdateRequest(EARLIESTPICKUPTIME, shippingPickup.earliestPickupTime));
        updateRequests.push(new PatchUpdateRequest(LATESTPICKUPTIME, shippingPickup.latestPickupTime));
        updateRequests.push(new PatchUpdateRequest(PICKUPCOMMENT, shippingPickup.pickupComment ?? null));

        if ((shippingPickup?.phone)) {
            updateRequests.push(new PatchUpdateRequest(PHONE, shippingPickup.phone));
        }

        return new Promise((resolve) => {
            this.orderCommandService.patchPickup(this.currentOrder.OrderCode, updateRequests).subscribe(data => {
                this.currentOrder.shipping.orderShippingPickup = shippingPickup;
                resolve();
            });
        });
    }

    patchPickup(propertyName: string, shippingPickup: OrderShippingPickup): Promise<number> {
        let patchReq: PatchUpdateRequest;
        switch (propertyName) {
            case COMPANY:
                patchReq = new PatchUpdateRequest(propertyName, shippingPickup.address.company);
                break;
            case NAME:
                patchReq = new PatchUpdateRequest(propertyName, shippingPickup.address.name);
                break;
            case PHONE:
                patchReq = new PatchUpdateRequest(propertyName, shippingPickup.address.phonenumber);
                break;
            case PICKUPDATE:
                patchReq = new PatchUpdateRequest(propertyName, shippingPickup.shipmentDate);
                break;
            case EARLIESTPICKUPTIME:
                patchReq = new PatchUpdateRequest(propertyName, shippingPickup.earliestPickupTime);
                break;
            case LATESTPICKUPTIME:
                patchReq = new PatchUpdateRequest(propertyName, shippingPickup.latestPickupTime);
                break;
            case PICKUPCOMMENT:
                patchReq = new PatchUpdateRequest(propertyName, shippingPickup.pickupComment);
                break;
            default:
                break;
        }

        return new Promise((resolve) => {
            this.orderCommandService.patchPickup(this.currentOrder.OrderCode, [patchReq])
                .subscribe(data => {
                    this.currentOrder.shipping.orderShippingPickup = shippingPickup;
                    resolve();
                });
        });
    }

    patchShippingDetails(propertyName: string, shippingDetails: OrderShippingDetails): Promise<number> {
        const patchReq: PatchUpdateRequest[] = [];
        switch (propertyName) {
            case ISDRYICESHIPMENT:
                patchReq.push(new PatchUpdateRequest(propertyName, shippingDetails.isDryIceShipment));
                if (shippingDetails.isDryIceShipment) {
                    patchReq.push(new PatchUpdateRequest(DRYICEWEIGHT, shippingDetails.dryIceWeight));
                    patchReq.push(new PatchUpdateRequest(DRYICEWEIGHTUNIT, shippingDetails.dryIceWeightUnit));
                } else {
                    patchReq.push(new PatchUpdateRequest(DRYICEWEIGHT, null));
                    patchReq.push(new PatchUpdateRequest(DRYICEWEIGHTUNIT, null));
                }
                break;
            case DRYICEWEIGHT:
                patchReq.push(new PatchUpdateRequest(propertyName, shippingDetails.dryIceWeight));
                patchReq.push(new PatchUpdateRequest(ISDRYICESHIPMENT, shippingDetails.isDryIceShipment));
                patchReq.push(new PatchUpdateRequest(DRYICEWEIGHTUNIT, shippingDetails.dryIceWeightUnit));
                break;
            case DRYICEWEIGHTUNIT:
                patchReq.push(new PatchUpdateRequest(propertyName, shippingDetails.dryIceWeightUnit));
                break;
            case ISRETURNSHIPMENTOPTED:
                patchReq.push(new PatchUpdateRequest(propertyName, shippingDetails.isReturnShipmentOpted));
                break;
            case SERVICEOPTION:
                patchReq.push(new PatchUpdateRequest(CARRIERSERVICECODE, shippingDetails.carrierServiceCode));
                patchReq.push(new PatchUpdateRequest(ESTIMATEDPRICE, shippingDetails.estimatedPrice));
                patchReq.push(new PatchUpdateRequest(BILLINGCURRENCYCODE, shippingDetails.billingCurrencyCode));
                break;
            case ISPICKUPREQUIRED:
                patchReq.push(new PatchUpdateRequest(ISPICKUPREQUIRED, shippingDetails.isPickUpRequired));
                break;
            default:
                break;
        }

        return new Promise((resolve) => {
            this.orderCommandService.patchShippingDetails(this.currentOrder.OrderCode, patchReq)
                .subscribe(data => {
                    if (propertyName === SERVICEOPTION) {
                        this.currentOrder.shipping.orderShippingDetails.carrierServiceName = shippingDetails?.carrierServiceName;
                        this.currentOrder.shipping.orderShippingDetails.carrierServiceCode = shippingDetails.carrierServiceCode;
                        this.currentOrder.shipping.orderShippingDetails.estimatedPrice = shippingDetails.estimatedPrice;
                        this.currentOrder.shipping.orderShippingDetails.billingCurrencyCode = shippingDetails.billingCurrencyCode;
                        this.currentOrder.shipping.orderShippingDetails.translationTag = shippingDetails.translationTag;
                    } else if (propertyName === ISRETURNSHIPMENTOPTED) {
                        this.currentOrder.shipping.orderShippingDetails.isReturnShipmentOpted = shippingDetails.isReturnShipmentOpted;
                    } else if (propertyName === ISDRYICESHIPMENT || propertyName === DRYICEWEIGHT || propertyName === DRYICEWEIGHTUNIT) {
                        this.currentOrder.shipping.orderShippingDetails.isDryIceShipment = shippingDetails.isDryIceShipment;
                        this.currentOrder.shipping.orderShippingDetails.dryIceWeight = shippingDetails.dryIceWeight;
                        this.currentOrder.shipping.orderShippingDetails.dryIceWeightUnit = shippingDetails.dryIceWeightUnit;
                    } else if (propertyName === ISPICKUPREQUIRED) {
                        this.currentOrder.shipping.orderShippingDetails.isPickUpRequired = shippingDetails.isPickUpRequired;
                    }
                    resolve();
                });
        });
    }

    async getPickupAddress(accountCode: string): Promise<Address> {
        const orderSetupContext = this.orderSetupService.getContext();
        const userDetails = await this.storeService.Get<UserDetails>(StoreKey.UserDetails).toPromise();
        const isInternalUser = userDetails.profile.userType.toString().toLowerCase() !== UserType[0].toString().toLowerCase();
        let address: Address;
        if (isInternalUser) {
            address = Object.assign({}, orderSetupContext?.transactionalData?.account?.address);
        } else {

            const accountContacts = await this.userManagementService.getAccountContacts(accountCode).toPromise();
            const matchedContact = accountContacts.find(x => x.email === userDetails.profile.email);
            const matchedContactAddress = matchedContact?.address;
            if (matchedContactAddress) {
                matchedContactAddress.name =
                    `${matchedContact?.greetings ?? ''} ${matchedContact?.firstName ?? ''} ${matchedContact?.lastName ?? ''}`.trim();
            }

            address = Object.assign({}, matchedContactAddress ?? orderSetupContext?.transactionalData?.account?.address);
            address.company = orderSetupContext?.transactionalData?.account?.name;
        }

        const matchedCountry = this.countryService.getCountryLists().find(e => e.name === address?.country || e.code === address?.country);
        const matchedState = this.countryService.getStateLists().find(e => e.name === address?.state || e.code === address?.state);
        address.country = (matchedCountry) ? matchedCountry?.code : null;
        address.state = (matchedState) ? matchedState?.code : null;
        address.isoCode = (matchedCountry) ? matchedCountry?.code : null;
        return address;
    }

    async getUserAndAccountSpecificAddress(accountCode: string){
        return await this.orderDataService.getUserAndAccountSpecificDetails(accountCode).toPromise();
    }

    async mapShipping() {
        let orderShippingDetails: OrderShippingDetails;

        // reset values
        this.currentOrderService.currentOrder.selfShipping = true;
        const shippingEstimates = this.currentOrderService.currentOrder.shipping?.orderShippingEstimates;
        const shippingDetails = this.currentOrderService.currentOrder.shipping?.orderShippingDetails;
        this.currentOrderService.currentOrder.shipping = null;

        try {
            // get shipping details
            const selectedOrderShipping = await this.orderDataService.getShippingDetails(this.currentOrder.OrderCode).toPromise();

            // map Shipping details to current order
            this.mapShippingToCurrentOrder(selectedOrderShipping, shippingEstimates, shippingDetails);

            // cleanup shipping details
            orderShippingDetails = await this.shippingDetailsCleanupService
                .CleanupShippingDetails(
                    this.currentOrderService.currentOrder?.OrderCode,
                    this.currentOrderService.currentOrder?.quotation?.receivingLabCode,
                    this.currentOrderService.currentOrder?.shipping?.orderShippingDetails,
                    shippingEstimates? shippingEstimates.length === 0: false);

        } catch (error) {/*When Shipping not available*/ }

        return orderShippingDetails;
    }

    private mapShippingToCurrentOrder(
        shipping: OrderShippingDetailsResponse,
        shippingEstimates: ShippingEstimate[],
        shippingDetails: OrderShippingDetails) {
        this.currentOrderService.currentOrder.shipping = new OrderShipping();
        const currentOrderShipping = this.currentOrderService.currentOrder.shipping;
        if (shipping) {
            this.currentOrderService.currentOrder.selfShipping = false;
            currentOrderShipping.orderShippingDetails = new OrderShippingDetails();
            currentOrderShipping.orderShippingDetails.isDryIceShipment = shipping.isDryIceShipment;
            currentOrderShipping.orderShippingDetails.isPickUpRequired = shipping.isPickUpRequired;
            currentOrderShipping.orderShippingDetails.dryIceWeightUnit = shipping.dryIceWeightUnit;
            currentOrderShipping.orderShippingDetails.dryIceWeight = shipping.dryIceWeight;
            currentOrderShipping.orderShippingDetails.isReturnShipmentOpted = shipping.isReturnShipmentOpted;
            currentOrderShipping.orderPackageInformation = new Map<number, OrderShippingPackage>();
            shipping.shippingPackagesDetail.forEach(Item => {
                let convertedWeightUnit;
                if (Item.weightUnit === WEIGHTUNITKG) {
                    convertedWeightUnit = PackageWeightUnitValue.KG;
                } else if (Item.weightUnit === WEIGHTUNITLB) {
                    convertedWeightUnit = PackageWeightUnitValue.LB;
                } else {
                    convertedWeightUnit = Item.weightUnit;
                }
                const orderShipping = new OrderShippingPackage(
                    Item.width,
                    Item.height,
                    Item.length,
                    Item.weight,
                    Item.quantity,
                    Item.dimensionUnit,
                    convertedWeightUnit
                );
                currentOrderShipping.orderPackageInformation.set(Item.id, orderShipping);
            });
            shipping.shippingPickupDetail.address.company = shipping.shippingPickupDetail.company;
            shipping.shippingPickupDetail.address.name = shipping.shippingPickupDetail.name;
            shipping.shippingPickupDetail.address.phonenumber = shipping.shippingPickupDetail.phone;
            shipping.shippingPickupDetail.address.isoCode = shipping.shippingPickupDetail.address.country;
            currentOrderShipping.orderShippingPickup
                = new OrderShippingPickup(null, '', '',
                    shipping.shippingPickupDetail.pickupComment, shipping?.shippingPickupDetail?.address);
            currentOrderShipping.orderShippingPickup.company = shipping.shippingPickupDetail.company;
            currentOrderShipping.orderShippingPickup.name = shipping.shippingPickupDetail.name;
            currentOrderShipping.orderShippingPickup.phone = shipping.shippingPickupDetail.phone;
            currentOrderShipping.orderShippingPickup.shipmentDate = shipping.shippingPickupDetail.pickupDate;
            currentOrderShipping.orderShippingPickup.earliestPickupTime = shipping.shippingPickupDetail.earliestPickupTime;
            currentOrderShipping.orderShippingPickup.latestPickupTime = shipping.shippingPickupDetail.latestPickupTime;
            if (shippingEstimates) {
                currentOrderShipping.orderShippingPickup.earliestPickupTime = shipping.shippingPickupDetail.earliestPickupTime;
                currentOrderShipping.orderShippingPickup.latestPickupTime = shipping.shippingPickupDetail.latestPickupTime;
                currentOrderShipping.orderShippingPickup.shipmentDate = shipping.shippingPickupDetail.pickupDate;
                currentOrderShipping.orderShippingEstimates = shippingEstimates;
                currentOrderShipping.orderShippingDetails.estimatedPrice = shippingDetails.estimatedPrice;
                currentOrderShipping.orderShippingDetails.carrierServiceCode = shippingDetails.carrierServiceCode;
                currentOrderShipping.orderShippingDetails.billingCurrencyCode = shippingDetails.billingCurrencyCode;
            }
        }
    }
}

export class PickUpTime {
    Date = new Date();
    From: number;
    To: number;
  
    constructor() {
      this.Date = new Date();
    }
  
    getTime(time: any) {
      if (time === 'From') { return this.formatTime(this.From); }
  
      return this.formatTime(this.To);
    }
  
    private formatTime(time: any) {
      if (Math.floor(time) === time && time > 9) { return `${time}00`; }
  
      if (Math.floor(time) === time && time <= 9) { return `0${time}00`; }
  
      if (Math.floor(time) !== time && time >= 10) { return `${Math.floor(time)}30`; }
  
      if (Math.floor(time) !== time && time < 10) { return `0${Math.floor(time)}30`; }
    }
  }
